import { Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { ModerationInboxComponent } from '../../shared/components/moderation-inbox.component';
import { AdReport, ReportSearch } from '../../models/ad-report.model';
import {
  selectCarReportForm,
  selectCarReportResults,
  selectCarSearchLoading,
} from '../../store/selectors/car.selectors';
import {
  selectRealEstateReportForm,
  selectRealEstateReportResults,
  selectRealEstateSearchLoading,
} from '../../store/selectors/real-estate.selectors';
import {
  searchCarReports,
  updateCarReport,
  updateCarReportSearchForm,
} from '../../store/actions/car.actions';
import {
  searchRealEstateReports,
  updateRealEstateReport,
  updateRealEstateReportSearchForm,
} from '../../store/actions/real-estate.actions';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject, take, takeUntil } from 'rxjs';
import { ReportStatuses } from '../../shared/constants/report-statuses.constants';
import { loadCategories } from '../../store/actions/categories.actions';
import {
  selectCategories,
  selectCategoriesLoading,
} from '../../store/selectors/categories.selectors';
import { selectIsArabic } from '../../store/selectors/language.selectors';

@Component({
  selector: 'app-reports',
  standalone: false,
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
})
export class ReportsComponent implements OnDestroy {
  private store = inject(Store);
  private fb = inject(FormBuilder);

  carReports$: Observable<AdReport[] | undefined>;
  realEstateReports$: Observable<AdReport[] | undefined>;
  loadingCarReports$!: Observable<boolean>;
  loadingRealEstateReports$!: Observable<boolean>;
  isArabic$: Observable<boolean> = this.store.select(selectIsArabic);
  categories$ = this.store.select(selectCategories);
  loadingCategories$ = this.store.select(selectCategoriesLoading);

  searchForm: FormGroup;
  isSearched = false;
  statuses = ReportStatuses;
  @ViewChild(ModerationInboxComponent) private moderationInbox?: ModerationInboxComponent;

  private destroy$ = new Subject<void>();

  constructor() {
    this.store.dispatch(loadCategories());
    this.carReports$ = this.store.select(selectCarReportResults);
    this.realEstateReports$ = this.store.select(selectRealEstateReportResults);
    this.searchForm = this.fb.group({
      reasons: [],
      statuses: [],
    });

    this.loadingCarReports$ = this.store.select(selectCarSearchLoading);
    this.loadingRealEstateReports$ = this.store.select(selectRealEstateSearchLoading);
  }

  ngOnInit(): void {
    this.store
      .select(selectRealEstateReportForm)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe((form) => {
        this.searchForm.patchValue(form as ReportSearch);
      });

    this.store
      .select(selectCarReportForm)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe((form) => {
        this.searchForm.patchValue(form as ReportSearch);
      });
  }

  search(): void {
    this.store.dispatch(updateCarReportSearchForm({ form: this.searchForm.value }));
    this.store.dispatch(updateRealEstateReportSearchForm({ form: this.searchForm.value }));
    this.store.dispatch(searchCarReports({ reportQuery: this.searchForm.value }));
    this.store.dispatch(searchRealEstateReports({ reportQuery: this.searchForm.value }));

    this.isSearched = true;
  }

  reset() {
    this.searchForm.reset();
    this.isSearched = false;
  }

  async updateCarReport(report: AdReport, status: string) {
    if (report.status === status) {
      return;
    }
    if (status === this.statuses.Pending) { await this.moveToPending(report, 'car'); return; }
    this.store.dispatch(
      updateCarReport({
        reportId: report.id,
        status,
      })
    );
  }

  async updateRealEstateReport(report: AdReport, status: string) {
    if (report.status === status) {
      return;
    }
    if (status === this.statuses.Pending) { await this.moveToPending(report, 'property'); return; }
    this.store.dispatch(
      updateRealEstateReport({
        reportId: report.id,
        status,
      })
    );
  }

  private async moveToPending(report: AdReport, kind: 'car' | 'property'): Promise<void> {
    const saved = await this.moderationInbox?.act({
      id: report.id, kind, source: 'LISTING', status: report.status,
      targetId: kind === 'car' ? report.car.id : report.realEstate.id,
      subject: kind === 'car' ? [report.car.model, report.car.name].filter(Boolean).join(' ') : report.realEstate.title,
      reason: report.reason, createdAt: new Date(report.createdAt).toISOString(),
    }, 'pending');
    if (saved) this.search();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
